import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Flex,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  VStack,
  HStack,
  Icon,
  Tooltip,
  Input,
} from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import { BsDownload } from "react-icons/bs";
import { useTranslation } from "react-i18next";

function CryptoReceiveModal({
  isOpen,
  onClose,
  receiveDetails,
  loading,
  walletAddress,
  network,
}) {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState(null);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const bgHover = useColorModeValue("gray.50", "navy.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const bgModal = useColorModeValue("white", "navy.800");
  const bgBox = useColorModeValue("gray.50", "navy.700");

  useEffect(() => {
    setQrCodeImage(receiveDetails?.qr_code?.image || null);
  }, [receiveDetails]);

  const handleCopy = useCallback((text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const downloadQRCode = useCallback(() => {
    if (!qrCodeImage) return;
    const link = document.createElement("a");
    link.href = qrCodeImage;
    link.download = `${receiveDetails?.symbol || "crypto"}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [qrCodeImage, receiveDetails?.symbol]);

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg={bgModal} borderRadius="20px">
          <Center h="400px">
            <VStack spacing="16px">
              <Spinner size="xl" color="brand.500" thickness="4px" />
              <Text fontSize="sm" color={textColorSecondary} fontWeight="500">
                {t("Generating receive details...")}
              </Text>
            </VStack>
          </Center>
        </ModalContent>
      </Modal>
    );
  }

  if (!receiveDetails) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent bg={bgModal} borderRadius="20px" boxShadow="2xl">
        <ModalHeader
          fontSize="2xl"
          fontWeight="700"
          color={textColor}
          pb="16px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          {t("Receive")} {receiveDetails?.symbol || t("Crypto")}
        </ModalHeader>
        <ModalCloseButton mt="8px" />

        <ModalBody py="20px">
          <VStack spacing="24px" align="stretch">
            <Box>
              <Flex justify="center" mb="12px">
                <Text fontSize="sm" fontWeight="600" color={textColorSecondary}>
                  {t("QR CODE")}
                </Text>
              </Flex>
              <Flex justify="center" align="center" py="16px">
                <Box bg={bgBox} p="12px" borderRadius="16px" border="2px solid" borderColor="brand.500">
                  {qrCodeImage && (
                    <img
                      src={qrCodeImage}
                      alt="QR Code"
                      style={{ width: "200px", height: "200px", borderRadius: "12px" }}
                    />
                  )}
                </Box>
              </Flex>
              <Flex justify="center" mt="12px">
                <Tooltip label={t("Download QR Code")} placement="bottom">
                  <Button
                    size="sm"
                    colorScheme="brand"
                    variant="outline"
                    leftIcon={<BsDownload />}
                    onClick={downloadQRCode}
                  >
                    {t("Download")}
                  </Button>
                </Tooltip>
              </Flex>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="600" color={textColorSecondary} mb="12px">
                {t("WALLET ADDRESS")}
              </Text>
              <HStack
                spacing="8px"
                bg={bgBox}
                p="12px"
                borderRadius="12px"
                border="1px solid"
                borderColor={borderColor}
                _hover={{ bg: bgHover }}
                cursor="pointer"
                onClick={() => handleCopy(receiveDetails?.address, "address")}
                transition="all 0.2s"
              >
                <Input
                  value={receiveDetails?.address}
                  isReadOnly
                  variant="unstyled"
                  fontSize="13px"
                  fontWeight="500"
                  color={textColor}
                  _focus={{ outline: "none" }}
                />
                <Tooltip
                  label={copiedField === "address" ? t("Copied!") : t("Copy Address")}
                  placement="top"
                >
                  <Box as="button" ml="auto">
                    <Icon
                      as={CopyIcon}
                      color={copiedField === "address" ? "green.500" : "brand.500"}
                      w="18px"
                      h="18px"
                      _hover={{ transform: "scale(1.1)" }}
                      transition="all 0.2s"
                    />
                  </Box>
                </Tooltip>
              </HStack>
            </Box>

            {receiveDetails?.transfer_network?.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="600" color={textColorSecondary} mb="12px">
                  {t("NETWORK DETAILS")}
                </Text>
                <VStack spacing="8px" align="stretch">
                  {receiveDetails.transfer_network.map((net, idx) => (
                    <Box
                      key={idx}
                      bg={bgBox}
                      p="16px"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={net.is_default ? "brand.500" : borderColor}
                    >
                      <Flex justify="space-between" align="flex-start" gap="16px">
                        <Box flex="1">
                          <Text fontSize="13px" fontWeight="700" color={textColor}>
                            {net.name}
                          </Text>
                          <Text fontSize="12px" color={textColorSecondary} mt="2px">
                            {net.network}
                          </Text>
                          <HStack spacing="4px" mt="8px">
                            <Box w="6px" h="6px" borderRadius="50%" bg="green.500" />
                            <Text fontSize="12px" fontWeight="500" color={textColor}>
                              {t("Active")}
                            </Text>
                          </HStack>
                        </Box>
                        <VStack align="flex-end" spacing="8px">
                          {net.is_default && (
                            <Box
                              bg="brand.500"
                              color="white"
                              fontSize="9px"
                              fontWeight="700"
                              px="8px"
                              py="4px"
                              borderRadius="6px"
                              whiteSpace="nowrap"
                            >
                              {t("DEFAULT")}
                            </Box>
                          )}
                          <VStack align="flex-end" spacing="2px">
                            <Text fontSize="13px" color={textColor} whiteSpace="nowrap" fontWeight="700">
                              {net.arrival_time || t("N/A")}
                            </Text>
                            <Text fontSize="11px" color={textColorSecondary} whiteSpace="nowrap" fontWeight="500">
                              {t("Arrival Time")}
                            </Text>
                          </VStack>
                        </VStack>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}

            {receiveDetails?.label && (
              <Box>
                <Text fontSize="sm" fontWeight="600" color={textColorSecondary} mb="12px">
                  {t("WALLET LABEL")}
                </Text>
                <Box
                  bg={bgBox}
                  p="12px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="14px" fontWeight="500" color={textColor}>
                    {receiveDetails.label}
                  </Text>
                </Box>
              </Box>
            )}

            <Box bg="blue.50" borderLeft="4px solid" borderColor="blue.500" p="12px" borderRadius="8px">
              <Text fontSize="12px" color="blue.800" fontWeight="500">
                ℹ️ {t("Share this QR code or address to receive")} {receiveDetails?.symbol}. {t("Make sure the network matches your preferred transfer method.")}
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor} pt="16px" gap="12px">
          <Button variant="ghost" colorScheme="gray" onClick={onClose} fontWeight="600" size="sm">
            {t("Close")}
          </Button>
          <Tooltip label={t("Copy address to clipboard")} placement="top">
            <Button
              colorScheme="brand"
              onClick={() => handleCopy(receiveDetails?.address, "footer")}
              leftIcon={<CopyIcon />}
              fontWeight="600"
              size="sm"
            >
              {copiedField === "footer" ? t("Copied!") : t("Copy Address")}
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default React.memo(CryptoReceiveModal);
